import { useState } from 'react'
import { ChevronDown, Pencil, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/** One labelled cell of a bordered settings grid (manufacturer terms, product facts). */
export function SettingCell({
  label,
  htmlFor,
  className,
  below,
  children,
}: {
  label: string
  htmlFor?: string
  className?: string
  below?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className={cn('min-w-0 border-b border-r px-4 py-3', className)}>
      {htmlFor ? (
        <Label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">
          {label}
        </Label>
      ) : (
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
      )}
      <div className="mt-1 flex min-h-8 min-w-0 items-center gap-1">{children}</div>
      {below}
    </div>
  )
}

export function ExpandableSettingCell({
  label,
  summary,
  className,
  readOnly = false,
  children,
}: {
  label: string
  summary: React.ReactNode
  className?: string
  /** Opens to details rather than an editor. */
  readOnly?: boolean
  children: (close: () => void) => React.ReactNode
}) {
  const [editing, setEditing] = useState(false)
  return (
    <SettingCell
      label={label}
      className={cn(className, editing && 'col-span-full')}
      below={
        editing ? (
          <div className="mt-2 max-w-2xl rounded-lg border bg-muted/30 p-3">
            {children(() => setEditing(false))}
          </div>
        ) : null
      }
    >
      <span className="min-w-0 text-sm font-medium">{summary}</span>
      <Button
        variant="ghost"
        size="icon"
        className="size-8 shrink-0"
        aria-expanded={editing}
        aria-label={
          editing
            ? `Close ${label.toLowerCase()}`
            : `${readOnly ? 'Show' : 'Edit'} ${label.toLowerCase()}`
        }
        onClick={() => setEditing((open) => !open)}
      >
        {editing ? (
          <X className="h-4 w-4" />
        ) : readOnly ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <Pencil className="h-3.5 w-3.5" />
        )}
      </Button>
    </SettingCell>
  )
}
